import React from 'react';
import ReactMarkdown from 'react-markdown';
import { User } from 'lucide-react';
import EditProposalCard from './EditProposalCard';

const AI_AVATAR = 'https://images.unsplash.com/photo-1609371497456-3a55a205d5eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMHdvbWFuJTIwaGVhZHNob3QlMjBwcm9mZXNzaW9uYWwlMjBlbGVnYW50JTIwZGFyayUyMGhhaXIlMjBuZXV0cmFsJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzYyMTI0ODB8MA&ixlib=rb-4.1.0&q=85&w=100&h=100&fit=crop';

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

export default function MessageBubble({ message, user, isStreaming, compact, agentName = 'Aria', editMap, onEditApproved }) {
  const isAssistant = message.role === 'assistant';
  const avatarSize = compact ? 30 : 34;
  const displayName = isAssistant ? agentName : (user?.name?.split(' ')[0] || 'You');

  // Split assistant content into prose + edit-proposal cards. Aria embeds
  // `[[aria-edit:edit_id]]` markers in her stored response (the raw JSON
  // <edit_proposal> blocks are stripped server-side). We also defensively
  // strip any raw blocks that slipped through during live streaming.
  const renderedContent = React.useMemo(() => {
    if (!isAssistant) return null;
    const raw = (message.content || '')
      // Hide any raw proposal JSON that may briefly appear during streaming
      .replace(/<edit_proposal>[\s\S]*?<\/edit_proposal>/g, '');
    const parts = [];
    const re = /\[\[aria-edit:([a-zA-Z0-9_-]+)\]\]/g;
    let lastIdx = 0;
    let m;
    while ((m = re.exec(raw)) !== null) {
      const before = raw.slice(lastIdx, m.index);
      if (before.trim()) parts.push({ type: 'md', text: before });
      parts.push({ type: 'edit', editId: m[1] });
      lastIdx = m.index + m[0].length;
    }
    const tail = raw.slice(lastIdx);
    if (tail.trim()) parts.push({ type: 'md', text: tail });
    if (parts.length === 0) parts.push({ type: 'md', text: raw });
    return parts;
  }, [isAssistant, message.content]);

  return (
    <div
      data-testid={`message-${message.message_id}`}
      style={{
        display: 'flex', gap: compact ? 10 : 12,
        padding: compact ? '10px 0' : '14px 0',
        animation: 'fadeIn 0.3s ease-out',
        alignItems: 'flex-start',
      }}
    >
      {/* Avatar — tight ring, dual-tone for assistant */}
      <div style={{
        width: avatarSize, height: avatarSize,
        borderRadius: '50%',
        overflow: 'hidden',
        boxShadow: isAssistant
          ? '0 0 0 1.5px var(--accent), 0 4px 10px rgba(124,58,237,0.18)'
          : '0 0 0 1.5px var(--border-medium)',
        flexShrink: 0, marginTop: 1,
        background: 'var(--bg-secondary)',
      }}>
        {isAssistant ? (
          <img src={AI_AVATAR} alt={agentName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : user?.picture ? (
          <img src={user.picture} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--bg-overlay)',
          }}>
            <User size={compact ? 13 : 15} color="var(--text-muted)" />
          </div>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Name row — proper-cased name + dot + relative time, modern hierarchy */}
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 8,
          marginBottom: compact ? 4 : 6,
        }}>
          <span style={{
            fontSize: compact ? 12 : 13, fontWeight: 600,
            color: isAssistant ? 'var(--text-primary)' : 'var(--text-primary)',
            letterSpacing: '-0.005em',
          }}>{displayName}</span>
          {isAssistant && (
            <span style={{
              fontSize: 10, fontWeight: 500,
              padding: '1px 6px',
              background: 'rgba(124,58,237,0.10)',
              color: 'var(--accent)',
              borderRadius: 999,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
            }}>AI</span>
          )}
          {message.created_at && (
            <span style={{
              fontSize: 10, color: 'var(--text-muted)',
              fontVariantNumeric: 'tabular-nums',
            }}>{formatTime(message.created_at)}</span>
          )}
        </div>

        {/* Message body */}
        <div style={{
          color: 'var(--text-primary)',
          fontSize: compact ? 13.5 : 14.5,
          lineHeight: 1.62,
          wordBreak: 'break-word',
        }}>
          {isAssistant ? (
            <div className="markdown-content">
              {renderedContent.map((part, idx) => {
                if (part.type === 'edit') {
                  const edit = editMap?.[part.editId];
                  if (!edit) {
                    // Edit not yet hydrated (rare race) — render a thin placeholder
                    return (
                      <div key={`edit-${idx}`} style={{
                        margin: '10px 0', padding: '10px 12px',
                        background: 'rgba(15,15,30,0.04)',
                        border: '1px dashed rgba(15,15,30,0.18)',
                        fontSize: 11, color: 'rgba(15,15,30,0.55)',
                        fontFamily: "'JetBrains Mono', monospace",
                      }}>Loading proposed edit…</div>
                    );
                  }
                  return (
                    <EditProposalCard
                      key={`edit-${part.editId}`}
                      edit={edit}
                      onApproved={onEditApproved}
                    />
                  );
                }
                return (
                  <ReactMarkdown
                    key={`md-${idx}`}
                    components={{
                  code({ node, inline, className, children, ...props }) {
                    const match = /language-(\w+)/.exec(className || '');
                    const lang = match ? match[1] : '';
                    if (!inline && (lang || String(children).includes('\n'))) {
                      return (
                        <div style={{ margin: '10px 0', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                          {lang && (
                            <div style={{
                              padding: '6px 12px', background: '#1a1a1a',
                              borderBottom: '1px solid var(--border-subtle)',
                              fontSize: 10, fontWeight: 600, color: 'var(--text-muted)',
                              textTransform: 'uppercase', letterSpacing: '0.04em',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}>
                              <span>{lang}</span>
                              <button onClick={() => navigator.clipboard.writeText(String(children))} style={{
                                background: 'none', border: 'none', color: 'var(--accent)',
                                cursor: 'pointer', fontSize: 10, padding: '1px 6px', fontFamily: 'inherit',
                              }}>Copy</button>
                            </div>
                          )}
                          <pre style={{
                            margin: 0, padding: compact ? 10 : 14, background: '#0A0A0A',
                            overflowX: 'auto', fontSize: compact ? 11 : 12, lineHeight: 1.5,
                            maxHeight: compact ? 200 : 400,
                          }}>
                            <code style={{ fontFamily: "'JetBrains Mono', monospace", color: '#e6edf3' }}>
                              {String(children).replace(/\n$/, '')}
                            </code>
                          </pre>
                        </div>
                      );
                    }
                    return (
                      <code style={{
                        background: 'var(--bg-overlay)', padding: '1px 6px', borderRadius: 4,
                        fontSize: '0.88em', fontFamily: "'JetBrains Mono', monospace",
                      }} {...props}>{children}</code>
                    );
                  },
                  p: ({ children }) => <p style={{ margin: '6px 0' }}>{children}</p>,
                  ul: ({ children }) => <ul style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ul>,
                  ol: ({ children }) => <ol style={{ margin: '6px 0', paddingLeft: 20 }}>{children}</ol>,
                  li: ({ children }) => <li style={{ marginBottom: 3 }}>{children}</li>,
                  h1: ({ children }) => <h1 style={{ fontSize: 18, fontWeight: 700, margin: '12px 0 6px', letterSpacing: '-0.01em' }}>{children}</h1>,
                  h2: ({ children }) => <h2 style={{ fontSize: 15, fontWeight: 600, margin: '10px 0 5px', letterSpacing: '-0.005em' }}>{children}</h2>,
                  h3: ({ children }) => <h3 style={{ fontSize: 13.5, fontWeight: 600, margin: '8px 0 4px', color: 'var(--text-secondary)' }}>{children}</h3>,
                  strong: ({ children }) => <strong style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</strong>,
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      borderLeft: '3px solid var(--accent)', paddingLeft: 12, margin: '8px 0', color: 'var(--text-secondary)',
                    }}>{children}</blockquote>
                  ),
                  a: ({ href, children }) => (
                    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{children}</a>
                  ),
                    }}
                  >
                    {part.text}
                  </ReactMarkdown>
                );
              })}
              {isStreaming && (
                <span style={{
                  display: 'inline-block', width: 6, height: 14, background: 'var(--accent)',
                  marginLeft: 3, borderRadius: 1,
                  animation: 'pulse-glow 1s infinite', verticalAlign: 'text-bottom',
                }} />
              )}
            </div>
          ) : (
            <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--text-secondary)' }}>{message.content}</p>
          )}
        </div>
      </div>
    </div>
  );
}
